/** @format */
import { isPlatformBrowser, NgClass, AsyncPipe, CurrencyPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	Inject,
	input,
	OnDestroy,
	OnInit,
	output,
	PLATFORM_ID,
	Signal,
	viewChild
} from '@angular/core';
import { HmfDrawerComponent, HmfSelectorComponent } from '@dsg-tech/homefield-angular';
import { Store } from '@ngrx/store';
import {
	AttributeOption,
	AttributesUtilityService,
	selectAttributePrices,
	selectDisabledAttributes,
	selectErrorList,
	selectSelectedAttributeOptions
} from '@pdp/attributes/data-access';
import { BreakpointsService } from '@shared/data-access/services';
import { BreakPointNew } from '@shared/data-access/enums';
import { filter, lastValueFrom, map, Subject, takeUntil } from 'rxjs';
import {
	HomefieldIconLibraryModule,
	homefieldIconAlertInfoFilled,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';

@Component({
	selector: 'pdp-attributes-components-size-selection-panel',
	templateUrl: './size-attributes-panel.component.html',
	styleUrl: './size-attributes-panel.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		HomefieldIconLibraryModule,
		HmfSelectorComponent,
		HmfDrawerComponent,
		NgClass,
		AsyncPipe,
		CurrencyPipe
	]
})
export class SizeAttributesPanelComponent implements OnInit, OnDestroy {
	private readonly breakpointsService = inject(BreakpointsService);

	sizeAttributeData = input.required<AttributeOption[]>();
	selectedAttributeOnClick = output<AttributeOption>();
	drawerComponent = viewChild(HmfDrawerComponent);
	drawerDirection: Signal<'right' | 'bottom'> = computed(() => {
		const breakpoint = this.breakpointsService.breakpoint();
		return breakpoint <= BreakPointNew.SMALL ? 'bottom' : 'right';
	});

	attributeName = computed(() => this.sizeAttributeData()[0]?.name);

	priceSizeMap = computed(() =>
		this.store.selectSignal(selectAttributePrices(this.attributeName()))
	);

	destroyed$: Subject<void> = new Subject();
	selectedAttributesBuffer: AttributeOption[] = [];
	selectDisabledAttributes$ = this.store.select(selectDisabledAttributes);
	selectErrorList$ = this.store.select(selectErrorList);
	selectedSize$ = this.store.select(selectSelectedAttributeOptions).pipe(
		takeUntil(this.destroyed$),
		filter(Boolean),
		map((options) => options.find((option) => option.name === this.attributeName()))
	);

	constructor(
		private store: Store,
		private readonly attributesUtilityService: AttributesUtilityService,
		@Inject(PLATFORM_ID) private readonly platformId,
		homefieldIconsLibraryService: HomefieldIconsLibraryService
	) {
		homefieldIconsLibraryService.registerIcons([homefieldIconAlertInfoFilled]);
	}

	ngOnInit(): void {
		if (isPlatformBrowser(this.platformId)) {
			this.selectErrorList$.pipe(takeUntil(this.destroyed$)).subscribe((errorList) => {
				const attributeKey = this.attributeName();
				const attributeErrorKeys = errorList ? Object.keys(errorList) : [];

				if (attributeErrorKeys.length && attributeErrorKeys[0] === attributeKey) {
					this.openDrawer();
				}
			});
		}
	}

	openDrawer(): void {
		this.drawerComponent()?.openDrawer();
	}

	closeDrawer(): void {
		this.drawerComponent()?.closeDrawer();
	}

	async checkAttributeErrorsOnClose() {
		lastValueFrom(this.selectErrorList$).then((errorList) => {
			const attributeKey = this.attributeName();
			const attributeErrorKeys = errorList ? Object.keys(errorList) : [];

			if (!attributeErrorKeys.length || !attributeErrorKeys.includes(attributeKey)) {
				this.attributesUtilityService.validateIfAttributeErrorsExist(
					'[size-attributes-panel] checkAttributeErrorsOnClose'
				);
			}
		});
	}

	selectedAttribute(selectedAttributeValue: AttributeOption): void {
		this.selectedAttributeOnClick.emit(selectedAttributeValue);
		this.closeDrawer();
	}

	trackByAttributeValue(attributeOption: AttributeOption): string {
		return attributeOption.value;
	}

	ngOnDestroy(): void {
		this.destroyed$.next();
		this.destroyed$.complete();
	}
}
